import { EnvVariable, getEnv } from 'lib/helpers/envHelper';
import { buildQueryParamLink } from 'lib/helpers/urlHelper';
import { ApiResponseError } from 'lib/logging';
import { redirect } from 'lib/redirect';
import { UnauthorizedErrorType } from 'lib/requests/types';
import _ from 'lodash';
import { NextPageContext } from 'next';
import { getSoftLogin } from './api';
import {
  buildUnauthorizedSoftLoginRedirectPath,
  parseRequestedSoftLoginPath,
} from './helpers';

interface ISoftLoginOptions {
  allowNotAuthed?: boolean;
}

/**
 * When modifying or extending this function, please be aware of the following
 * "session" types and their interaction with the `UnauthorizedErrorType` enum:
 *
 * 1. No session      - The user is not logged in.
 * 2. Key session     - The user is using a `key` query param to access the LC.
 * 3. SSO session     - The user is using SSO to access the LC.
 * 4. SCORM session   - The user is doing our training through an LMS using a
 *                      downloadable SCORM package.
 * 5. Unwired session
 */
export const softLogin = async (
  context: NextPageContext,
  callback: (context: NextPageContext) => Promise<any>,
  options?: ISoftLoginOptions
) => {
  const { basePath, otherParams, params } = parseRequestedSoftLoginPath(
    context.asPath
  );

  /**
   * When the `scormKey` query param is present, we are in scorm mode and
   * don't need to perform a soft login. Scorm is loaded inside of an iframe, and
   * we can't use cookies like we could if the learning center was loaded normally.
   * Safari blocks 3rd party cookies by default.
   */
  if (params.scormKey) {
    return callback(context);
  }

  const { data, errorType, status, headers } = await getSoftLogin(params.key);

  const handleSuccess = () => {
    if (data?.isAuthCookieSet) {
      if (context.res) {
        context.res.setHeader('Set-Cookie', headers['set-cookie']);
      }

      if (params.sso) {
        return redirect(
          buildQueryParamLink(`${getEnv(EnvVariable.API_PATHNAME)}/login`, {
            redirectPath: buildQueryParamLink('/learning/choose-your-account', {
              path: buildQueryParamLink(basePath, otherParams),
            }),
            sso: params.sso,
          })
        );
      }
    }

    if (params.key) {
      return redirect(buildQueryParamLink(basePath, otherParams));
    }
  };

  const handleStatus = async () => {
    switch (status) {
      case 200:
        return handleSuccess();
      case 401:
        if (options?.allowNotAuthed) {
          if (
            _.isUndefined(params.sso) &&
            errorType === UnauthorizedErrorType.NOT_AUTHED
          ) {
            return callback(context);
          }
          if (errorType === UnauthorizedErrorType.NO_PROFILE_FOUND) {
            return callback(context);
          }
        }
        return redirect(
          buildUnauthorizedSoftLoginRedirectPath({
            asPath: context.asPath,
            errorType,
          })
        );
      default:
        throw new ApiResponseError('Unexpected status code');
    }
  };

  await handleStatus();

  const props = await callback(context);

  return { ...props, useSoftLogin: data?.isAuthCookieSet };
};
